/* eslint-disable indent */
/* eslint-disable react/forbid-prop-types */
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useNavigate } from 'react-router-dom';
import { DatePicker, TextareaInput } from 'components/FormControl';
import { formatDatetime } from 'utils/func';
import { checkPermissionForRegisterResult } from 'utils/func/permission';
import { REGISTER_RESULT_STATUSES } from 'constant';

function RegisterResultCommment({
  onSubmit,
  handleSubmit,
  control,
  errors,
  data,
  disabled,
  isEditComment,
  status,
  isSumup,
  setApproveModalVisible,
  setVisible,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formatDeadline = (value) => {
    const datetime = formatDatetime(value.deadline, 'DD/MM/YYYY HH:mm:ss');
    return <span className="text-color-secondary">{datetime}</span>;
  };

  const formatComment = (value) => {
    if (!value?.comment) return null;

    return (
      <div>
        {value?.comment.map((comment) => (
          <div
            style={{ whiteSpace: 'pre-wrap' }}
            className="text-color-secondary mb-2"
            key={comment?.id}
          >
            -----------------------------------------------
            <span className="block mb-2">
              ({comment?.email} - {formatDatetime(comment?.createdAt, 'DD/MM/YYYY HH:mm:ss')})
            </span>
            {comment?.content}
          </div>
        ))}
      </div>
    );
  };

  const formatFeedback = (value) => (
    <span style={{ whiteSpace: 'pre-wrap' }} className="text-color-secondary">
      {value.feedback}
    </span>
  );

  const formatApproveLabel = useMemo(() => {
    if (status === REGISTER_RESULT_STATUSES.SUBMITTED) {
      return t('registerResult.action.institutionalApprove');
    }
    if (status === REGISTER_RESULT_STATUSES.INSTITUTIONAL_SPECIALIST_REVIEW) {
      return t('registerResult.action.specialistApprove');
    }
    if (status === REGISTER_RESULT_STATUSES.SPECIALIST_REVIEW) {
      return t('registerResult.action.departmentLeaderApprove');
    }
    return null;
  }, [status, t]);

  return (
    <div className="detail-info-council">
      <h4 className="mb-0">{t('registerResult.requestUpdate.history')}</h4>

      <div className="ml-4 mt-2">
        {data?.length > 0 ? (
          <DataTable
            value={data}
            className="p-datatable-gridlines header-table"
            scrollable
            showGridlines
            emptyMessage={t('registerResult.viewHistory.empty')}
            // isLoading={isLoading}
          >
            <Column
              field="comment"
              header={t('registerResult.viewHistory.comment')}
              style={{ minWidth: '16rem', textAlign: 'left' }}
              body={formatComment}
            />
            <Column
              field="feedback"
              header={t('registerResult.viewHistory.feedback')}
              style={{ minWidth: '16rem', textAlign: 'left' }}
              body={formatFeedback}
            />
            <Column
              field="deadline"
              header={t('registerResult.viewHistory.deadline')}
              body={formatDeadline}
              style={{ minWidth: '5rem', maxWidth: '8rem', textAlign: 'left' }}
            />
          </DataTable>
        ) : (
          <span className="text-color-secondary">
            {t('registerResult.viewHistory.noCommentFromSpecialist')}
          </span>
        )}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`p-fluid ${data?.length > 0 ? 'mt-4' : 'mt-2'}`}
        >
          <div className="mb-2">
            <TextareaInput
              name="comment"
              control={control}
              errors={errors}
              row={6}
              label={t('projectAdjustment.requestUpdate.commentFromSpecialist')}
            />
          </div>
          <div className="w-16rem mb-2 font-semibold">
            <DatePicker
              label={t('registerResult.requestExplanation.deadline')}
              name="deadline"
              control={control}
              errors={errors}
              defaultValue={new Date()}
            />
          </div>

          <div className="flex justify-content-end gap-2">
            {!isSumup && (
              <div className="flex justify-content-end gap-4 mt-2">
                <Button
                  className="w-10rem"
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  label={t('extendImplementationTime.comeBack')}
                  severity="danger"
                  type="button"
                  onClick={() => {
                    navigate(-1);
                  }}
                />
                <Button
                  type="button"
                  label={t('registerResult.reviewHistory.title')}
                  className="w-14rem"
                  onClick={() => setVisible(true)}
                />
                {checkPermissionForRegisterResult(status) && (
                  <Button
                    className="w-16rem"
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    label={formatApproveLabel}
                    severity="success"
                    type="button"
                    onClick={() => setApproveModalVisible(true)}
                  />
                )}
                <Button
                  label={t('registerResult.requestExplanation.requestUpdate')}
                  className="w-14rem"
                  severity={isEditComment ? 'warning' : 'info'}
                  type="submit"
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

RegisterResultCommment.propTypes = {
  onSubmit: PropTypes.func,
  handleSubmit: PropTypes.func,
  control: PropTypes.object,
  errors: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.array,
  disabled: PropTypes.bool,
  isEditComment: PropTypes.bool,
  status: PropTypes.string,
  isSumup: PropTypes.bool,
  setApproveModalVisible: PropTypes.func,
  setVisible: PropTypes.func,
};

RegisterResultCommment.defaultProps = {
  onSubmit: () => {},
  handleSubmit: () => {},
  control: {},
  errors: {},
  data: [],
  disabled: false,
  isEditComment: false,
  status: '',
  isSumup: false,
  setApproveModalVisible: () => {},
  setVisible: () => {},
};

export default React.memo(RegisterResultCommment);
